import React, { useEffect, useState, useMemo, useRef } from "react";
import HamBurgerIcon from "../../../Assets/Icons/Group 49.svg";
import profileImagePlaceholderBlue from "../../../Assets/Images/PersonBlue.svg";
import profileImagePlaceholderAdmin from "../../../Assets/Images/Ellipse 3.svg";
import BellIcon from "../../../Assets/Icons/Group 51.svg";
import "./navbar.scss";
import { Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import SearchBar from "../SearchBar";
import Notifications from "./Notifications";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../services/authService";
import { getFcmToken } from "../../../utils/connectStore";
import Loader from "../Loader";
import debounce from "lodash.debounce";
import { getOffersByMerchant, getNonDraftedOffers } from "../../../services/offerService";
import { getFranchises } from "../../../services/franchiseService";
import { getMerchantBusinessProfile } from "../../../services/merchantService";
import { getFundraiserProfile } from "../../../services/fundraiserService";
// import NotificationPopup from "../NotificationPopup";

const Navbar = ({ isSideBarOpen, setisSideBarOpen, element }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isBellClicked, setIsBellClicked] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [navSearchQuery, setNavSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const loading = useSelector((state) => state.authReducer.loading);
  const userData = useSelector((state) => state?.authReducer?.tokenData?.user);
  const selectedMerchant = useSelector((state) => state?.merchantReducer?.selectedMerchent);
  const merchantProfiles = useSelector((state) => state?.merchantReducer?.merchantProfiles?.data);
  const [profileLogoFromApi, setProfileLogoFromApi] = useState(null);

  // Fetch the active merchant profile logo directly from API when selection changes
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const role = (userData?.role || "").toString().toLowerCase();
        if (role === "fundraiser") {
          const res = await getFundraiserProfile();
          if (res?.success && res?.data) {
            // Guard: only allow approved fundraisers to stay logged in
            const statusRaw = res?.data?.status;
            const status = typeof statusRaw === 'string' ? statusRaw.toLowerCase() : statusRaw;
            if (status !== 'approved') {
              // Force logout and redirect to fundraiser login with message
              const msg = status === 'declined'
                ? 'Your account has been declined. Please contact support.'
                : 'Your account is pending admin approval. Please wait for approval.';
              try { await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap(); } catch(_) {}
              navigate('/fundraiser/account-login', { replace: true, state: { message: msg } });
              return;
            }
            setProfileLogoFromApi(res?.data?.logo || res?.data?.profileImage || null);
            return;
          }
        }
        // Skip logo fetch for admin users when they're just selecting merchants for offer creation
        if (role === "admin") {
          setProfileLogoFromApi(null);
          return;
        }
        // Merchant branch
        const targetId = selectedMerchant?._id || userData?.merchantId || userData?.merchant?._id;
        if (targetId) {
          const res = await dispatch(getMerchantBusinessProfile({ id: targetId }))?.unwrap();
          if (res?.success && res?.data) {
            setProfileLogoFromApi(res?.data?.logo || null);
            return;
          }
        }
        setProfileLogoFromApi(null);
      } catch (_) {
        setProfileLogoFromApi(null);
      }
    };
    fetchLogo();
    
    // Listen for profile update events and refresh logo
    const handleProfileUpdate = () => {
      fetchLogo();
    };
    window.addEventListener('profile-updated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [dispatch, selectedMerchant?._id, userData?.merchantId, userData?.role, merchantProfiles]);
  const userRole = (userData?.role || "").toString().toLowerCase();
  // const unreadCount = useSelector((state) => state?.notificationReducer?.unreadCount);

  const [showBackdrop, setShowBackdrop] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef(null);
  // const [demoNotification, setDemoNotification] = useState(null);

  const handleLogoutClick = async () => {
    // Store the user role before logout since it will be cleared
    const currentUserRole = userRole;
    
    const logoutPromise = await dispatch(
      logout({ fcmToken: getFcmToken() })
    )?.unwrap();
    if (logoutPromise?.success === true) {
      try {
        localStorage.removeItem('profilePic');
        localStorage.removeItem('LATEST_UPLOADED_LOGO');
        sessionStorage.removeItem('ADMIN_OFFER_CREATION_GUARD');
      } catch(_) {}
      // Navigate to appropriate login page based on user role
      if (currentUserRole && currentUserRole.toLowerCase() === "fundraiser") {
        navigate("/fundraiser/account-login");
      } else {
        // Navigate cleanly to the login page without query params
        navigate("/");
      }
      // Avoid hard reloads to preserve target route and ensure smooth UX
    }
  };

  const guardedNav = (path) => {
    if (sessionStorage.getItem("ADMIN_OFFER_CREATION_GUARD") === "true") {
      window.dispatchEvent(new CustomEvent("attempt-exit-offer-form", { detail: { path } }));
      return;
    }
    navigate(path);
  };

  const guardedLogout = async () => {
    if (sessionStorage.getItem("ADMIN_OFFER_CREATION_GUARD") === "true") {
      window.dispatchEvent(new CustomEvent("attempt-exit-offer-form", { detail: { action: "logout" } }));
      return;
    }
    await handleLogoutClick();
  };

  const items = [
    {
      key: 1,
      label: <p onClick={() => {guardedNav(`/${userData?.role}/edit-profile`)}}>Profile Settings</p>,
    },
    {
      key: 2,
      label: <p onClick={guardedLogout}>Logout</p>,
    }
  ];

  const onBellClicked = async () => {
    const newState = !isBellClicked;
    setIsBellClicked(newState);

    if (newState) {
      setShowBackdrop(true);
      // Prevent scrolling by fixing the body
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    } else {
      setTimeout(() => {
        setShowBackdrop(false);
      }, 300);
      // Restore scrolling
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.height = "100%";
    }
  };

  const handleBackdropClick = () => {
    // Close notifications when clicking outside
    setIsBellClicked(false);
    setTimeout(() => {
      setShowBackdrop(false);
    }, 300);
  };

  const handleGreeting = () => {
    const now = new Date();
    const hour = now.getHours();

    if (hour < 12) {
      setGreeting("Good Morning");
    } else if (hour < 18) {
      setGreeting("Good Afternoon");
    } else {
      setGreeting("Good Evening");
    }
  };

  useEffect(() => {
    handleGreeting();
  }, []);

  // Search functionality for merchants
  const performSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    try {
      const results = [];

      // Search offers
      if (userRole === "merchant") {
        try {
          const offersResponse = await dispatch(getOffersByMerchant({
            search: query,
            limit: 5,
            silent: true
          })).unwrap();
          
          if (offersResponse?.success && offersResponse?.data) {
            offersResponse.data.forEach(offer => {
              results.push({
                id: offer._id,
                type: 'offer',
                title: offer.title,
                description: `Offer: ${offer.title}`,
                status: offer.status,
                category: offer.category?.title,
                action: () => navigate(`/merchant/offer-list`, { state: { openOfferId: offer._id } })
              });
            });
          }
        } catch (error) {
          console.log("Error searching offers:", error);
        }

        // Search franchises/stores
        try {
          const franchisesResponse = await dispatch(getFranchises({
            search: query,
            limit: 5,
            silent: true
          })).unwrap();
          
          if (franchisesResponse?.success && franchisesResponse?.data) {
            franchisesResponse.data.forEach(franchise => {
              results.push({
                id: franchise._id,
                type: 'franchise',
                title: franchise.name,
                description: `Store: ${franchise.name}`,
                status: franchise.status,
                action: () => navigate(`/merchant/franchise-list`)
              });
            });
          }
        } catch (error) {
          console.log("Error searching franchises:", error);
        }
      }

      // Search for admin role
      if (userRole === "admin") {
        try {
          // Admin should not call merchant-only endpoint; use non-drafted offers search
          const offersResponse = await dispatch(getNonDraftedOffers({
            search: query,
            limit: 5,
            silent: true,
          })).unwrap();

          if (offersResponse?.success && offersResponse?.data) {
            offersResponse.data.forEach(offer => {
              results.push({
                id: offer._id,
                type: 'offer',
                title: offer.title,
                description: `Offer: ${offer.title}`,
                status: offer.status,
                category: offer.category?.title,
                action: () => navigate(`/offer-list`, { state: { openOfferId: offer._id } })
              });
            });
          }
        } catch (error) {
          // Swallow errors during live search; let final actions surface errors if needed
          console.log("Error searching offers:", error);
        }
      }

      // Search for fundraiser role
      if (userRole === "fundraiser") {
        // Add fundraiser-specific search functionality here
        // For now, we'll add a placeholder result
        results.push({
          id: 'fundraiser-search',
          type: 'fundraiser',
          title: 'Fundraiser Search',
          description: `Searching for: ${query}`,
          action: () => navigate(`/fundraiser/dashboard`)
        });
      }

      setSearchResults(results);
    } catch (error) {
      console.log("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const debouncedSearch = useMemo(() => {
    const fn = debounce((q) => {
      const trimmed = (q || "").trim();
      if (trimmed.length === 0) {
        setSearchResults([]);
        setShowSearchResults(true);
        setIsSearching(false);
        return;
      }
      performSearch(trimmed);
    }, 350);
    return fn;
  }, [userRole]);

  const handleNavbarSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      const trimmed = (navSearchQuery || "").trim();
      if (trimmed.length === 0) return;
      // If we have results, prefer first result action; otherwise perform a fresh search
      if (searchResults.length > 0) {
        searchResults[0].action?.();
      } else {
        performSearch(trimmed);
      }
      setShowSearchResults(false);
      // keep query so user can refine after navigate; clear if you prefer
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setNavSearchQuery(value);
    debouncedSearch(value);
  };

  const handleSearchResultClick = (result) => {
    result.action();
    setShowSearchResults(false);
    setNavSearchQuery("");
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow for clicks
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200);
  };

  // Cleanup debounced search on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Ensure sidebar is closed by default on mobile and closes on resize to mobile
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        // Close on mount if mobile
        if (window.innerWidth <= 768) {
          setisSideBarOpen(false);
        }
        const handleResize = () => {
          if (window.innerWidth <= 768) {
            setisSideBarOpen(false);
          }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }
    } catch (_) {}
  }, []);

  // Close mobile search when clicking outside
  useEffect(() => {
    if (!mobileSearchOpen) return;
    const onDocClick = (e) => {
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) {
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [mobileSearchOpen]);

  return (
    <div
      className={
        isSideBarOpen
          ? "sm:w-[70%] md:w-[75%] lg:w-[80%] xl:w-[80%] overflow-y-auto bg-[#FBFBFB] relative"
          : "w-full overflow-y-auto bg-[#FBFBFB] relative"
      }
    >
      {loading && <Loader />}
      <div className="flex py-4 bg-white items-center justify-between px-4 sm:px-6 shadow-[0px_8px_20px_-12px_rgba(0,0,0,0.35)] relative z-0">
        <div className="flex items-center gap-3">
          <div>
            <img
              src={HamBurgerIcon}
              className="mr-2 cursor-pointer"
              onClick={() => {
                setisSideBarOpen(!isSideBarOpen);
              }}
            alt="menu"
            ></img>
          </div>
          {(userRole === "admin" || userRole === "fundraiser" || userRole === "merchant") && (
            <div className="relative hidden sm:block w-[200px] sm:w-[280px] md:w-[320px] lg:w-[340px]">
              <SearchBar
                parentDivClass="w-full"
                divClassName="flex items-center w-full h-[40px] relative"
                inputClassName="navSearchBar h-full w-[100%] ps-[4%] pe-[14%] text-[12px] font-[400] outline-none bg-[#F5F5F5] text-[#303030]"
                iconClassName="absolute right-[5%]"
                placeholder="Search here"
                value={navSearchQuery}
                handleChange={handleSearchChange}
                handleKeyDown={handleNavbarSearchKeyDown}
                onBlur={handleSearchBlur}
                searchResults={searchResults}
                resultsVisible={showSearchResults}
                noResultsText={isSearching ? "Searching..." : "No results found"}
                handleResultClick={handleSearchResultClick}
                onIconClick={() => {
                  const q = (navSearchQuery || "").trim();
                  if (q.length === 0) {
                    setSearchResults([]);
                    setShowSearchResults(true);
                    return;
                  }
                  performSearch(q);
                }}
              />
              {isSearching && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-md p-2 text-center text-sm text-gray-500">
                  Searching...
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 sm:gap-4 pr-4 sm:pr-6">
          {(userRole === "admin" || userRole === "fundraiser" || userRole === "merchant") && (
            <button
              type="button"
              aria-label="Search"
              className="sm:hidden p-2 rounded-md"
              onClick={() => setMobileSearchOpen(true)}
            >
              {/* Themed search icon - lighter brand color */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#8EDAF5"/>
              </svg>
            </button>
          )}
          <div className="flex flex-col items-center justify-start relative">
            <img
              src={BellIcon}
              className="bg-[#00ADEF0D] p-2 cursor-pointer"
              onClick={onBellClicked}
              alt="notifications"
            />
          </div>

          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            className="ms-8 cursor-pointer"
          >
            <div className="flex" style={{ borderLeft: "1px solid #E0E0E0" }}>
              <div className="ps-6 pe-4">
                <p
                  className="text-[14px] text-right font-[500] whitespace-nowrap"
                  style={{ lineHeight: "21px" }}
                >
                  {userData?.name}
                </p>
                <p className="text-[12px] text-[#8C8C8C] font-[500] whitespace-nowrap">
                  {greeting}
                </p>
              </div>

              <div className="">
                {(function(){
                  // Determine image src and whether it is the placeholder
                  const pick = () => {
                    const apiLogo = profileLogoFromApi;
                    if (apiLogo && typeof apiLogo === 'string' && apiLogo.trim() !== '') {
                      const isAbs = /^(https?:)?\/\//i.test(apiLogo);
                      return { src: isAbs ? apiLogo : `${process.env.REACT_APP_S3_URL || ''}${apiLogo}`, isPlaceholder: false };
                    }
                    const selLogo = selectedMerchant?.logo;
                    if (selLogo && typeof selLogo === 'string' && selLogo.trim() !== '') {
                      const isAbs = /^(https?:)?\/\//i.test(selLogo);
                      return { src: isAbs ? selLogo : `${process.env.REACT_APP_S3_URL || ''}${selLogo}`, isPlaceholder: false };
                    }
                    const listLogo = (Array.isArray(merchantProfiles) ? merchantProfiles[0]?.logo : undefined) ||
                      userData?.logo || userData?.merchant?.logo || userData?.fundraiser?.logo ||
                      userData?.merchantProfileLogo || userData?.fundraiserProfileLogo;
                    if (listLogo && typeof listLogo === 'string' && listLogo.trim() !== '') {
                      const isAbs = /^(https?:)?\/\//i.test(listLogo);
                      return { src: isAbs ? listLogo : `${process.env.REACT_APP_S3_URL || ''}${listLogo}`, isPlaceholder: false };
                    }
                    return { src: profileImagePlaceholderBlue, isPlaceholder: true };
                  };
                  const picked = pick();
                  const isAdmin = userRole === "admin";
                  const isPlaceholder = picked.isPlaceholder === true;
                  const src = isPlaceholder ? (isAdmin ? profileImagePlaceholderAdmin : profileImagePlaceholderBlue) : picked.src;
                  // Size rules:
                  // - Blue placeholder (no logo, non-admin): smaller 40/34
                  // - Real logos and admin ellipse placeholder: larger 48/44
                  const isSmallPlaceholder = isPlaceholder && !isAdmin;
                  const imgSizeClass = isSmallPlaceholder ? 'w-[34px] h-[34px]' : 'w-[44px] h-[44px]';
                  const boxSizeClass = isSmallPlaceholder ? 'w-[40px] h-[40px]' : 'w-[48px] h-[48px]';
                  return (
                    <div className={`${boxSizeClass} rounded-full flex items-center justify-center ${isSmallPlaceholder ? 'bg-[#E6F7FF] border border-[#00ADEF33]' : ''}`}>
                      <img
                        src={src}
                        alt="profile"
                        className={`${imgSizeClass} rounded-full object-cover object-top`}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Main Layout Route Pages Section */}
      <div className="px-5 pt-5 pb-8 relative">
        {element}

        {/* Demo notification removed */}

        {/* Backdrop for when notifications panel is open */}
        {showBackdrop && (
          <div
            className={`fixed inset-0 bg-black bg-opacity-0 z-40 transition-opacity duration-300 ${
              isBellClicked ? "opacity-0" : "opacity-0"
            }`}
            onClick={handleBackdropClick}
          ></div>
        )}

        {/* Always render the Notifications component but control visibility with props */}
        <Notifications isVisible={isBellClicked} onClose={() => setIsBellClicked(false)} />
      </div>
      {/* Click-catcher to close sidebar on mobile (no visual backdrop) */}
      {isSideBarOpen && (
        <div
          className="fixed inset-0 z-[900] lg:hidden"
          onClick={() => setisSideBarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile expanding search input (overlay at top) */}
      {mobileSearchOpen && (
        <div className="sm:hidden fixed top-0 left-0 right-0 z-[950] bg-white px-4 py-3 shadow" ref={mobileSearchRef}>
          <div className="relative w-full">
            <SearchBar
              parentDivClass="w-full"
              divClassName="flex items-center w-full h-[40px] relative"
              inputClassName="navSearchBar h-full w-[100%] ps-[4%] pe-[14%] text-[14px] font-[400] outline-none bg-[#F5F5F5] text-[#303030]"
              iconClassName="absolute right-[5%]"
              placeholder="Search here"
              value={navSearchQuery}
              handleChange={handleSearchChange}
              handleKeyDown={handleNavbarSearchKeyDown}
              onBlur={handleSearchBlur}
              searchResults={searchResults}
              resultsVisible={showSearchResults}
              noResultsText={isSearching ? "Searching..." : "No results found"}
              handleResultClick={handleSearchResultClick}
              onIconClick={() => {
                const q = (navSearchQuery || "").trim();
                if (q.length === 0) {
                  setSearchResults([]);
                  setShowSearchResults(true);
                  return;
                }
                performSearch(q);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
